import { useState } from 'react';
import { Empty, Modal, Pagination } from 'antd';
import QuickTalentPoolReqModal from '../QuickTalentPoolReqModal';
import { useAuth } from '../../../providers/AuthProvider';
import { useAPI } from '../../../config/API';
import LoadingBanner from '../../../components/LoadingBanner';
import { Link } from 'react-router-dom'

const RequisitionsList = ({ talentPool, onReqCreated }) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const { auth } = useAuth();

    const clientId = auth.clientOrg?.()?.id;
    const poolId = talentPool?.id;

    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 3;

    const {
        data,
        isFetching,
    } = useAPI(['fetchTalentPoolActiveProjects', { clientId, poolId, page: currentPage, per_page: pageSize }], {
        enabled: !!clientId && !!poolId,
        staleTime: 60000,
    });

    const requisitions = data?.requisitions || [];
    const total = data?.total || 0;
    const lastPage = data?.last_page || 1;


    return (
        <>
            <div className="min-h-auto bg-gradient-to-br from-slate-50 via-white to-indigo-50/30 p-3 sm:p-6 mb-6">

                <div className="max-w-7xl mx-auto">
                    {/* Header */}
                    <div className={TB.mapClass('flex items-center justify-between', { 'mb-4 sm:mb-6': requisitions?.length > 0 })}>
                        <div>
                            <div className="text-[10px] sm:text-xs font-semibold text-secondary tracking-wider mb-1">
                                OPEN REQS
                            </div>
                            <h1 className="text-lg sm:text-2xl font-bold text-slate-900">

                                {(isFetching && !data) ? (
                                    <span>Loading Reqs...</span>
                                ) : (
                                    <span>{total} Active {TB.pluralize('Req', total)}</span>
                                )}

                            </h1>
                        </div>

                        <button
                            className="btn btn-primary"
                            onClick={() => setIsModalOpen(true)}
                            disabled={isFetching}
                        >
                            + Open Req
                        </button>
                    </div>

                    {requisitions?.length > 0 && (
                        <div className="space-y-3">
                            {requisitions.map((req) => (
                                <div
                                    key={req.id}
                                    className="bg-white shadow-sm hover:shadow-md transition-all duration-300 p-3 sm:p-4 border border-slate-100"
                                >
                                    <div className="flex flex-wrap items-center gap-3 sm:gap-6">
                                        {/* Job Title & Status - Left */}
                                        <div className="flexitems-center gap-2 sm:gap-3 flex-1 min-w-0">
                                            <h2 className="text-sm sm:text-base font-bold text-slate-900 truncate">
                                                {req.positions?.[0].title}
                                            </h2>
                                            <div className="flex gap-3 pt-2">
                                                <span className="text-xxs text-slate-600 whitespace-nowrap hidden sm:inline">
                                                    Open {TB.moment(req.setup_at).fromNow()}
                                                </span>
                                                <span className="text-xxs text-slate-400 whitespace-nowrap hidden lg:inline">
                                                    Last activity {TB.moment(req.updated_at).fromNow()}
                                                </span>
                                            </div>
                                        </div>

                                        {/* Metrics - Center */}
                                        <div className="flex items-center gap-4 sm:gap-6">
                                            <MetricColumn
                                                label="NOTIFIED"
                                                value={req.positions[0]?.matching_talent_count || 0}
                                            />
                                            <MetricColumn
                                                label="INVITED"
                                                value={req.shortlisted_candidates_count || 0}
                                            />
                                            <MetricColumn
                                                label="INTERESTED"
                                                value={req.interested_candidates_count || 0}
                                            />
                                            <MetricColumn
                                                label="PROPOSALS"
                                                value={req.candidate_proposals_received_count || 0}
                                                highlight={req.candidate_proposals_received_count > 0}
                                            />
                                        </div>

                                        {/* Action Buttons - Right */}
                                        <div className="flex items-center gap-2">
                                            <Link className="btn btn-primary" to={req.detailLink()}>
                                                View Req
                                            </Link>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {lastPage > 1 && (
                        <div className="flex justify-end mt-4">
                            <Pagination
                                current={currentPage}
                                pageSize={pageSize}
                                total={total}
                                onChange={(page) => setCurrentPage(page)}
                                showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} reqs`}
                                showSizeChanger={false}
                            />
                        </div>
                    )}
                </div>
            </div>

            {/* Quick Req Modal */}
            <Modal
                open={isModalOpen}
                onCancel={() => setIsModalOpen(false)}
                closable={false}
                footer={false}
                destroyOnClose={true}
                wrapClassName="[&_.ant-modal-body]:p-0"
                width="90%"
            >
                {isModalOpen && (
                    <QuickTalentPoolReqModal
                        talentPool={talentPool}
                        onSuccess={(project) => {
                            setIsModalOpen(false);
                            onReqCreated?.(project);
                        }}
                        onClose={() => setIsModalOpen(false)}
                    />
                )}
            </Modal>
        </>
    );
};

function MetricColumn({ label, value, highlight = false }) {
    return (
        <div className="flex flex-col items-center gap-1">
            <div
                className={TB.mapClass('text-xl sm:text-2xl font-bold transition-colors duration-200', {
                    'text-emerald-500': highlight,
                    'text-slate-900': !highlight
                })}
            >
                {value}
            </div>
            <div className="text-[9px] sm:text-[10px] font-semibold text-slate-400 tracking-wider">
                {label}
            </div>
        </div>
    );
}

export default RequisitionsList;